// Pure search logic: match, rank, and group. No React / no side effects.

import { SearchItem, SearchTypeFilter, ResultGroup } from "./types";
import { expandQuery } from "./synonyms";
import { expandAliases, normalizeSearchText } from "./aliases";
import {
  WEATHER_TOKEN_TO_PARAM,
  WEATHER_SUGGEST_WORDS,
  FORECAST_TOKENS,
} from "./deepLink";

// Canonical forecast words a partial token can complete to.
const FORECAST_SUGGEST_WORDS = ["forecast", "prediction"];

/**
 * Resolve a partial (still-being-typed) modifier token. Returns the weather
 * param or `{forecast:true}` when the token is a ≥2-char prefix of exactly one
 * canonical weather word or "forecast". Lets "bagawas rai" already behave like
 * "bagawas rain" before the user finishes typing.
 */
function resolvePartialModifier(
  token: string,
): { weatherParam?: string; forecast?: boolean } | null {
  if (token.length < 2) return null;
  if (WEATHER_TOKEN_TO_PARAM[token] || FORECAST_TOKENS.has(token)) return null; // already complete
  const wHits = WEATHER_SUGGEST_WORDS.filter((w) => w.startsWith(token));
  const fHit = FORECAST_SUGGEST_WORDS.some((w) => w.startsWith(token));
  // Unambiguous single interpretation only.
  if (wHits.length === 1 && !fHit)
    return { weatherParam: WEATHER_TOKEN_TO_PARAM[wHits[0]] };
  if (fHit && wHits.length === 0) return { forecast: true };
  return null;
}

/**
 * Pull weather + forecast modifier tokens out of a query, leaving the entity
 * text behind. Lets a user type "ABC feeder temperature forecast" → search the
 * entity "ABC feeder", carrying the temperature param + the forecast flag.
 * The LAST token is also matched as a partial prefix ("rai" → rain) so the
 * pairing kicks in before the modifier word is fully typed.
 * Returns null when neither modifier is present.
 */
function extractModifiers(
  normalized: string,
): { weatherParam: string | null; forecast: boolean; rest: string } | null {
  const tokens = normalized.split(/\s+/).filter(Boolean);
  let weatherParam: string | null = null;
  let forecast = false;
  const rest: string[] = [];
  tokens.forEach((t, i) => {
    const param = WEATHER_TOKEN_TO_PARAM[t];
    if (param && weatherParam == null) {
      weatherParam = param; // first weather token = the param
      return;
    }
    if (FORECAST_TOKENS.has(t)) {
      forecast = true;
      return;
    }
    // Only the final token may be a partial modifier, and only if there's
    // already some entity text before it (so a lone "rai" still searches normally).
    if (i === tokens.length - 1 && rest.length > 0) {
      const partial = resolvePartialModifier(t);
      if (partial?.weatherParam && weatherParam == null) {
        weatherParam = partial.weatherParam;
        return;
      }
      if (partial?.forecast) {
        forecast = true;
        return;
      }
    }
    rest.push(t);
  });
  if (!weatherParam && !forecast) return null;
  return { weatherParam, forecast, rest: rest.join(" ").trim() };
}

/** True when the item navigates to a Chart view that can show a weather param. */
function hasChartTarget(item: SearchItem): boolean {
  if (item.hasDualViews) return !!item.chartTarget;
  return item.viewKind === "Chart" || !!item.deepLink;
}

const TYPE_SCORE: Record<SearchItem["type"], number> = {
  action: 400,
  page: 300,
  entity: 200,
  metric: 100,
};

/** Inline cap before a category collapses into an expandable group. */
export const INLINE_GROUP_CAP = 6;
export const MAX_RESULTS = 10;

// Match tiers, scored so a worse tier can never outrank a better one regardless
// of type/position bonuses (tier gap 10000 >> max type+pos+priority sum).
export type MatchKind =
  | "exact"
  | "prefix"
  | "contains"
  | "alias"
  | "synonym"
  | "fuzzy";
// Tier gaps (10000) >> max type+pos+priority sum, so a worse tier never
// outranks a better one. Aliases sit below literal contains but above plain
// synonyms (and well above fuzzy).
const TIER_SCORE: Record<MatchKind, number> = {
  exact: 60000,
  prefix: 50000,
  contains: 40000,
  alias: 30000,
  synonym: 20000,
  fuzzy: 10000,
};

interface Scored {
  item: SearchItem;
  score: number;
  matchPos: number;
  matchKind: MatchKind;
}

/** Levenshtein edit distance (iterative, two-row). Small strings only. */
export function levenshtein(a: string, a2: string): number {
  if (a === a2) return 0;
  const m = a.length;
  const n = a2.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === a2[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

// Allowed typo budget scales with word length: ~1 edit per 4 chars, capped.
function fuzzyThreshold(len: number): number {
  if (len <= 3) return 0; // too short to fuzzy safely
  if (len <= 5) return 1;
  if (len <= 9) return 2;
  return 3;
}

/**
 * Does `query` fuzzily match any whitespace-delimited token of `text`?
 * Returns true when some token is within the typo budget for the query length.
 */
function fuzzyTokenMatch(query: string, text: string): boolean {
  const budget = fuzzyThreshold(query.length);
  if (budget === 0) return false;
  for (const token of text.split(/\s+/)) {
    if (!token) continue;
    // Quick length gate before the O(n*m) distance.
    if (Math.abs(token.length - query.length) > budget) continue;
    if (levenshtein(query, token) <= budget) return true;
  }
  return false;
}

function scoreItem(
  item: SearchItem,
  query: string,
  synonymTerms: string[],
  aliasTerms: string[],
): Scored | null {
  // Normalize index sides the SAME way the query is normalized, so punctuation
  // differences (e.g. "pvt.LTD" vs query "pvtltd") don't break literal matches.
  const label = normalizeSearchText(item.label);
  const text = normalizeSearchText(item.searchText);

  // Tier 1-3: exact / prefix / contains against the literal query.
  let pos = label.indexOf(query);
  const textPos = text.indexOf(query);
  if (pos < 0 || (textPos >= 0 && textPos < pos)) pos = textPos;
  let matchKind: MatchKind | null = null;
  if (pos >= 0) {
    if (label === query) matchKind = "exact";
    else if (label.startsWith(query) || text.startsWith(query))
      matchKind = "prefix";
    else matchKind = "contains";
  }

  // Space-insensitive literal match: ignore ALL whitespace on both sides so an
  // entity named "I T I" is found by "iti", and "i t i" by typing it spaced.
  // Only runs when the spaced literal missed, and only when collapsing spaces
  // actually changes the query (so normal single-word queries skip this).
  if (matchKind == null) {
    const qNoSpace = query.replace(/\s+/g, "");
    if (qNoSpace.length >= 2) {
      const labelNoSpace = label.replace(/\s+/g, "");
      const textNoSpace = text.replace(/\s+/g, "");
      const lp = labelNoSpace.indexOf(qNoSpace);
      const tp = textNoSpace.indexOf(qNoSpace);
      if (lp >= 0 || tp >= 0) {
        if (labelNoSpace === qNoSpace) matchKind = "exact";
        else if (labelNoSpace.startsWith(qNoSpace) || textNoSpace.startsWith(qNoSpace))
          matchKind = "prefix";
        else matchKind = "contains";
        // Position bonus is approximate (space-stripped offset); start-of-string
        // hits still score highest, which is what matters for ranking.
        pos = lp >= 0 ? lp : tp;
      }
    }
  }

  let best = pos >= 0 ? pos : Infinity;

  // Tier 4: alias (real-world spellings/jargon/abbreviations → canonical terms).
  if (matchKind == null) {
    for (const term of aliasTerms) {
      if (term === query) continue;
      const sp = text.indexOf(term);
      const lp = label.indexOf(term);
      const p = Math.min(sp < 0 ? Infinity : sp, lp < 0 ? Infinity : lp);
      if (p < best) best = p;
    }
    if (best !== Infinity) matchKind = "alias";
  }

  // Tier 5: synonym (non-literal terms only).
  if (matchKind == null) {
    for (const term of synonymTerms) {
      if (term === query) continue;
      const sp = text.indexOf(term);
      const lp = label.indexOf(term);
      const p = Math.min(sp < 0 ? Infinity : sp, lp < 0 ? Infinity : lp);
      if (p < best) best = p;
    }
    if (best !== Infinity) matchKind = "synonym";
  }

  // Tier 6: fuzzy (typo tolerance) as a last resort.
  if (matchKind == null && fuzzyTokenMatch(query, text)) {
    matchKind = "fuzzy";
    best = 0;
  }

  if (matchKind == null) return null;

  let score = TIER_SCORE[matchKind] + TYPE_SCORE[item.type];
  if (best === 0) score += 100;
  else if (best <= 5) score += 80;
  else if (best <= 20) score += 50;
  score += item.priority;

  return { item, score, matchPos: best === Infinity ? 0 : best, matchKind };
}

export interface RankedItem extends SearchItem {
  /** True when this result was matched only by fuzzy (typo) matching. */
  approximate?: boolean;
}

/**
 * Run a query against the index. Returns a flat ranked list with type filtering,
 * synonym expansion, and fuzzy fallback. Fuzzy results carry `approximate: true`
 * and always rank below exact/prefix/contains/synonym matches.
 */
export function searchIndex(
  query: string,
  index: SearchItem[],
  typeFilter: SearchTypeFilter,
): RankedItem[] {
  const normalized = normalizeSearchText(query);
  if (!normalized) return [];

  const mods = extractModifiers(normalized);

  // "entity + modifier" (e.g. "ABC feeder temperature", "badawas forecast"):
  // search the entity on the remaining text and carry the weather param and/or
  // forecast flag onto chart-capable results. Only engages when there's a real
  // entity remainder — a bare modifier falls through to the normal path so the
  // weather/forecast metric result still shows.
  if (mods && mods.rest.length > 0) {
    const synonymTerms = expandQuery(mods.rest);
    const aliasTerms = expandAliases(mods.rest);
    const scored: Scored[] = [];
    for (const item of index) {
      if (typeFilter !== "all" && item.type !== typeFilter) continue;
      if (!hasChartTarget(item)) continue; // weather/forecast apply on Chart
      const s = scoreItem(item, mods.rest, synonymTerms, aliasTerms);
      if (s) scored.push(s);
    }
    // The modifier word may actually be part of an entity NAME (e.g. the plant
    // "Akshvi Solar Line" — "solar" is a weather token). Stripping it then leaves
    // a non-contiguous remainder that matches nothing. When the modifier-stripped
    // search finds nothing, fall through to the normal full-query path below so
    // the literal name still matches.
    if (scored.length === 0) {
      // fall through to normal path (do not return)
    } else {
    scored.sort((a, b) => b.score - a.score || a.matchPos - b.matchPos);
    // Weather applies on Chart only; forecast works on BOTH Chart (fc=1) and the
    // Metric forecast tab. So when weather is present we collapse a dual item to
    // a single Chart result; for forecast-only we KEEP both views (the Metric URL
    // gets tab=forecast appended in buildNavTarget).
    const chartOnly = !!mods.weatherParam;
    return scored.map((s) => {
      const base: SearchItem =
        chartOnly && s.item.hasDualViews
          ? {
              ...s.item,
              hasDualViews: false,
              viewKind: "Chart",
              page: s.item.chartTarget?.page ?? s.item.page,
              pageLabel: s.item.chartTarget?.pageLabel ?? s.item.pageLabel,
              deepLink: s.item.chartTarget?.deepLink ?? s.item.deepLink,
              chartTarget: undefined,
              metricTarget: undefined,
              directQuery: undefined,
            }
          : { ...s.item };
      return {
        ...base,
        ...(mods.weatherParam ? { weatherParam: mods.weatherParam } : {}),
        ...(mods.forecast ? { forecast: true } : {}),
        ...(s.matchKind === "fuzzy" ? { approximate: true } : {}),
      };
    });
    }
  }

  const synonymTerms = expandQuery(normalized);
  const aliasTerms = expandAliases(normalized);
  const scored: Scored[] = [];

  for (const item of index) {
    if (typeFilter !== "all" && item.type !== typeFilter) continue;
    const s = scoreItem(item, normalized, synonymTerms, aliasTerms);
    if (s) scored.push(s);
  }

  scored.sort((a, b) => b.score - a.score || a.matchPos - b.matchPos);
  return scored.map((s) => {
    let item = s.item;
    // Bare weather query → attach the param to weather metric results so the
    // Chart page enables the Weather variable + that param on navigate.
    if (mods?.weatherParam && item.category === "Weather") {
      item = { ...item, weatherParam: mods.weatherParam };
    }
    return s.matchKind === "fuzzy" ? { ...item, approximate: true } : item;
  });
}

/**
 * Predictive suggestion layer — lightweight, runs BEFORE full ranking so the
 * palette feels assistive after only 2–4 characters (Raycast/Spotlight style).
 * Pure prefix/substring on the normalized query, no fuzzy/alias/synonym work:
 *   1) `startsWith` matches first (label, then any whitespace token of the label)
 *   2) `includes` matches next
 * Across ALL types (action/page/entity/metric), de-duplicated by id, capped.
 * O(n) over the index; far cheaper than `searchIndex`.
 */
export function getPredictiveSuggestions(
  query: string,
  index: SearchItem[],
  limit = 6,
): SearchItem[] {
  const q = normalizeSearchText(query);
  if (q.length < 2) return [];
  // Space-insensitive variant so "iti" finds an "I T I" entity here too.
  const qNoSpace = q.replace(/\s+/g, "");

  const prefix: SearchItem[] = [];
  const contains: SearchItem[] = [];
  for (const item of index) {
    const label = normalizeSearchText(item.label);
    const labelNoSpace = label.replace(/\s+/g, "");
    const textNoSpace = normalizeSearchText(item.searchText).replace(/\s+/g, "");
    if (
      label.startsWith(q) ||
      label.split(/\s+/).some((t) => t.startsWith(q)) ||
      labelNoSpace.startsWith(qNoSpace)
    ) {
      prefix.push(item);
    } else if (
      label.includes(q) ||
      normalizeSearchText(item.searchText).includes(q) ||
      labelNoSpace.includes(qNoSpace) ||
      textNoSpace.includes(qNoSpace)
    ) {
      contains.push(item);
    }
  }
  // Within each bucket, keep index order but float higher-priority types first
  // (action > page > entity > metric) so commands/pages lead the suggestions.
  const byType = (a: SearchItem, b: SearchItem) =>
    TYPE_SCORE[b.type] - TYPE_SCORE[a.type];
  prefix.sort(byType);
  contains.sort(byType);
  return [...prefix, ...contains].slice(0, limit);
}

/**
 * Smart grouping: keep the top-ranked items inline (rank order, capped at
 * MAX_RESULTS), but when a single category floods the results, collapse its
 * overflow into an expandable bucket. No result is ever dropped — anything not
 * shown inline is reachable via its category group.
 *
 * `groups` are returned in rank order (by their best-ranked member) so the
 * visual sequence matches the flat list. Items pushed out of `flat` by the
 * MAX_RESULTS cap are folded back into their category group.
 */
export function groupResults(ranked: SearchItem[]): {
  flat: SearchItem[];
  groups: ResultGroup[];
} {
  const order = new Map(ranked.map((it, i) => [it.id, i]));

  // Bucket by category, preserving rank order within each bucket.
  const byCategory = new Map<string, SearchItem[]>();
  for (const item of ranked) {
    const arr = byCategory.get(item.category) ?? [];
    arr.push(item);
    byCategory.set(item.category, arr);
  }

  const inline: SearchItem[] = [];
  const overflow = new Map<string, SearchItem[]>();

  for (const [category, items] of byCategory) {
    if (items.length > INLINE_GROUP_CAP) {
      inline.push(...items.slice(0, INLINE_GROUP_CAP));
      overflow.set(category, items.slice(INLINE_GROUP_CAP));
    } else {
      inline.push(...items);
    }
  }

  // Rank-order the inline list, then enforce MAX_RESULTS. Anything trimmed by
  // the cap is moved into its category's overflow rather than discarded.
  inline.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
  const flat = inline.slice(0, MAX_RESULTS);
  for (const trimmed of inline.slice(MAX_RESULTS)) {
    const arr = overflow.get(trimmed.category) ?? [];
    arr.unshift(trimmed); // keep ahead of already-overflowed items
    overflow.set(trimmed.category, arr);
  }

  // Build groups in rank order (by best-ranked member, re-sorted internally).
  const groups: ResultGroup[] = Array.from(overflow.entries())
    .filter(([, items]) => items.length > 0)
    .map(([category, items]) => ({
      category,
      items: items
        .slice()
        .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0)),
      collapsed: true,
    }))
    .sort(
      (a, b) =>
        (order.get(a.items[0].id) ?? 0) - (order.get(b.items[0].id) ?? 0),
    );

  return { flat, groups };
}

/**
 * Highlight the matching term within `text`. Considers the literal query and
 * its synonym expansions, highlighting whichever term actually appears (so a
 * "demand" query still highlights "Load …" when matched via synonym). Picks the
 * earliest, longest hit.
 */
export function highlightMatch(
  text: string,
  query: string,
): { text: string; hit: boolean }[] {
  const normalized = normalizeSearchText(query);
  if (!normalized) return [{ text, hit: false }];
  const lower = text.toLowerCase();

  let bestIdx = Infinity;
  let bestLen = 0;
  // Literal query + synonym expansions + alias canonical terms all participate,
  // so an alias hit (e.g. "fdr" → feeder) highlights the matched word.
  const terms = [
    normalized,
    ...expandQuery(normalized),
    ...expandAliases(normalized),
  ];
  for (const term of terms) {
    if (!term) continue;
    const idx = lower.indexOf(term);
    if (idx >= 0 && (idx < bestIdx || (idx === bestIdx && term.length > bestLen))) {
      bestIdx = idx;
      bestLen = term.length;
    }
  }
  if (bestIdx === Infinity) return [{ text, hit: false }];

  return [
    { text: text.slice(0, bestIdx), hit: false },
    { text: text.slice(bestIdx, bestIdx + bestLen), hit: true },
    { text: text.slice(bestIdx + bestLen), hit: false },
  ].filter((p) => p.text.length > 0);
}
